import { type Player } from "@blings/blings-player";
import {
  createRecreateSession,
  EditorRecreateStaleError,
  type EditorRecreateSession,
} from "@blings/blings-player/editor-tools";
import { debounce } from "lodash";
import { cleanEmptyStringInValue } from "../helpers/objects";
import PlayerManager from "../helpers/playerManager";
import { getENV } from "./consts";
import type { SettingsApiEnv } from "@blings/blings-player/lib/src/fetchData/formatApiUrl";
import type { ISdkParams } from "@blings/blings-player/lib/src/SDK/sdk.api";
import {
  detachLayerInspector,
  detachLayerInspectorForPlayer,
} from "../helpers/layerInspectorTools";
import {
  PLAYER_RECREATE_DEBOUNCE_MS,
  applyStudioScenePatchBeforeConcat,
  buildRecreateChanges,
  cloneForPlayer,
  createConfigSignature,
  createStudioScenePatch,
  getNormalizedProjectCacheInputs,
  getRecreateCacheInputs,
  getRecreateOptions,
  isAnimationOnlyRecreate,
  omitAnimationChange,
  type PlayerRunConfig,
  type StudioScenePatch,
} from "./runPlayerCache";

type StudioPlayerWindow = Window & { p?: Player | null };

const resizePlayer = (p: Player) => {
  let maxHeight = window.innerHeight;
  let widthPrecent = 100;
  if (p.mainContainer.offsetHeight > maxHeight) {
    widthPrecent = (maxHeight / p.mainContainer.offsetHeight) * 100;
    document
      .getElementsByClassName("VidContainerWrapper-inner-container")[0]
      .setAttribute(
        "style",
        "max-width:" + widthPrecent + "%; max-height:" + maxHeight
      );
  }
};

/** This objects will contain all the player configuration and the running instance for this player. */
let oldConfig: PlayerRunConfig = {};
let newConfig: PlayerRunConfig = {};
let player: Player | null = null;
let beingCreated = false;
let recreateSession: EditorRecreateSession | undefined;
let recreateSessionPlayerVersion: number | undefined;
let recreateSessionContainer: HTMLElement | null = null;
let lastAppliedConfigSignature = "";
let pendingConfigSignature = "";
let pendingForceRecreate = false;
let lastAppliedProjectCacheInputs: unknown;
let lastAppliedRecreateCacheInputs:
  | ReturnType<typeof getRecreateCacheInputs>
  | undefined;
let sessionLifecycleId = 0;

const createSessionPlayerFactory = (
  playerVersion: number,
  isCurrent: () => boolean
) => {
  let shouldReplaceExistingPlayer = true;

  return (playerParams: ISdkParams) => {
    const newPlayer = !shouldReplaceExistingPlayer;
    shouldReplaceExistingPlayer = false;

    return PlayerManager.get().createPlayer(playerParams, {
      playerMajorVersion: playerVersion,
      playerFromInstalledModule: true,
      newPlayer,
      isCurrent,
    });
  };
};

const destroyPlayerInstance = (playerToDestroy?: Player | null) => {
  try {
    playerToDestroy?.destroy();
  } catch (error) {
    console.error("err destroying player", error);
  }
};

const destroySession = (
  sessionToDestroy?: EditorRecreateSession,
  playerToDestroy?: Player | null
) => {
  const activePlayer = sessionToDestroy?.getPlayer() ?? playerToDestroy;
  if (activePlayer) {
    detachLayerInspectorForPlayer(activePlayer);
  } else {
    detachLayerInspector();
  }

  if (sessionToDestroy) {
    sessionToDestroy.destroy();
  } else {
    destroyPlayerInstance(activePlayer);
  }

  if (typeof window !== "undefined") {
    const studioWindow = window as StudioPlayerWindow;
    if (studioWindow.p === activePlayer) {
      studioWindow.p = null;
    }
  }
};

const destroyRecreateSession = ({
  resetPending = true,
}: { resetPending?: boolean } = {}) => {
  destroySession(recreateSession, player);

  recreateSession = undefined;
  recreateSessionPlayerVersion = undefined;
  recreateSessionContainer = null;
  player = null;
  lastAppliedProjectCacheInputs = undefined;
  lastAppliedRecreateCacheInputs = undefined;
  lastAppliedConfigSignature = "";

  if (resetPending) {
    pendingConfigSignature = "";
    pendingForceRecreate = false;
  }
};

const buildPlayerParams = (
  config: PlayerRunConfig,
  vidContainer: HTMLElement
): ISdkParams => {
  const width = config.jsonFile?.w;
  const height = config.jsonFile?.h;
  const ratio =
    typeof width === "number" && typeof height === "number"
      ? width / height
      : undefined;
  const settings = {
    skin: {
      timeline: {
        show: true,
      },
    },
    jsonConfig: config.JsonConfig,

    maxLoadingTime: 10000,
    autoplay: config.autoplay,
    posterFrame: config.posterFrame,
    startFrame: config.startFrame,
    ratio,
    rendererType: config.rendererType,
    hideCover: true,
  };
  const studioScenePatch = createStudioScenePatch(config);
  const playerParams: Partial<ISdkParams> = {
    data: cleanEmptyStringInValue(JSON.parse(JSON.stringify(config.videoData))),
    settings: {
      container: vidContainer,
      analyticsConfig: { disable: true },
      ...settings,
      fontOverrides: config.fontOverrides,
    },
    experiments: cloneForPlayer(config.experiments),
    variantSelectionSet: cloneForPlayer(config.variantSelectionSet),
    scenes: config.scenes,
    flowDiagram: cloneForPlayer(config.flowDiagram),
    rest: {
      hooks: {
        changeSceneBeforeConcatHooks: [
          (_scenes: unknown[], videoParts: StudioScenePatch[]) =>
            applyStudioScenePatchBeforeConcat(studioScenePatch, videoParts),
        ],
      },
    },
  };
  if (config.playerVersion === 3) {
    playerParams.project = {
      liveControlData: cleanEmptyStringInValue(
        JSON.parse(JSON.stringify(config.liveControlData))
      ),
      videoParts: cloneForPlayer(config.videoParts),
    };
  } else {
    playerParams.project = {
      env: getENV() as SettingsApiEnv,
      id: config.projectId,
      liveControlData: cleanEmptyStringInValue(
        JSON.parse(JSON.stringify(config.liveControlData))
      ),
      videoParts: cloneForPlayer(config.videoParts),
    };
  }

  return playerParams as ISdkParams;
};

/**
 * Create or recreate the editor player on a vid-container div.
 * The first call creates an editor recreate session. Later calls feed config
 * changes through that session so the player can preserve frame/playback state.
 * @param {object} config All the configuration for the player to be built
 */
export const runPlayer = async ({
  projectId,
  JsonConfig,
  jsonFile,
  autoplay,
  posterFrame,
  startFrame,
  scenes,
  rendererType,
  videoData,
  liveControlData,
  videoParts,
  cb,
  onError,
  assetChanges,
  playerVersion,
  experiments,
  variantSelectionSet,
  forceRecreate,
  flowDiagram,
  fontOverrides,
  animationChange,
}: PlayerRunConfig) => {
  // Save the received parameters as the new configuration for the player
  // This will be used to check if the player needs to be recreated and recreated if needed
  newConfig = {
    JsonConfig,
    jsonFile,
    autoplay,
    posterFrame,
    startFrame,
    scenes,
    rendererType,
    videoData,
    liveControlData,
    videoParts,
    cb,
    onError,
    assetChanges,
    playerVersion,
    experiments,
    variantSelectionSet,
    flowDiagram,
    fontOverrides,
    forceRecreate,
    animationChange,
  };
  pendingConfigSignature = createConfigSignature(newConfig);
  pendingForceRecreate = pendingForceRecreate || Boolean(forceRecreate);

  //#region --------------->>>  START THE PLAYER CREATION PROCESS  <<<------------------------------
  // Check if the player is being created
  if (beingCreated) return;

  if (
    recreateSession &&
    !forceRecreate &&
    lastAppliedConfigSignature === pendingConfigSignature
  ) {
    return;
  }

  // Set the player is being created to true and save the new config as the old config as a copy
  beingCreated = true;
  const lifecycleId = sessionLifecycleId;
  const runningConfigSignature = pendingConfigSignature;
  const runningForceRecreate = pendingForceRecreate;
  pendingForceRecreate = false;
  oldConfig = { ...newConfig };
  oldConfig.forceRecreate = runningForceRecreate;
  const runningPlayerVersion = oldConfig.playerVersion ?? -1;
  const runningProjectCacheInputs = getNormalizedProjectCacheInputs(oldConfig);
  const runningRecreateCacheInputs = getRecreateCacheInputs(oldConfig);

  // Get the vid-container div and check if it exists.
  const vidContainer = document.getElementById("vid-container");
  const spinner = document.getElementById("loader-inner-container");
  if (!vidContainer) {
    beingCreated = false;
    return;
  }

  const playerParams = buildPlayerParams(oldConfig, vidContainer);
  const shouldCreateNewSession =
    !recreateSession ||
    runningForceRecreate ||
    recreateSessionPlayerVersion !== runningPlayerVersion ||
    recreateSessionContainer !== vidContainer;

  try {
    if (shouldCreateNewSession) {
      destroyRecreateSession({ resetPending: false });
      spinner?.setAttribute("style", "");
      vidContainer.innerHTML = "";
      const nextSession = await createRecreateSession(playerParams, {
        createPlayer: createSessionPlayerFactory(
          runningPlayerVersion,
          () => lifecycleId === sessionLifecycleId
        ),
      });
      if (lifecycleId !== sessionLifecycleId) {
        destroySession(nextSession);
        return;
      }
      recreateSession = nextSession;
      recreateSessionPlayerVersion = runningPlayerVersion;
      recreateSessionContainer = vidContainer;
      player = recreateSession.player ?? null;
    } else {
      const currentSession = recreateSession as EditorRecreateSession;
      detachLayerInspectorForPlayer(currentSession.getPlayer());
      const recreateChanges = buildRecreateChanges(
        playerParams,
        oldConfig,
        lastAppliedProjectCacheInputs,
        lastAppliedRecreateCacheInputs
      );
      const recreateOptions = getRecreateOptions(oldConfig, runningForceRecreate);
      let result;
      try {
        result = await currentSession.recreate(
          recreateChanges,
          recreateOptions
        );
      } catch (error) {
        if (!isAnimationOnlyRecreate(recreateChanges)) {
          throw error;
        }

        console.warn("[studio-recreate-animation-fallback]", error);
        result = await currentSession.recreate(
          omitAnimationChange(recreateChanges),
          {
            ...recreateOptions,
            strategy: "full-recreate",
          }
        );
      }
      if (lifecycleId !== sessionLifecycleId) {
        destroySession(currentSession, result.player);
        return;
      }
      player = result.player;
    }

    // After the player is created, resize it to the correct size and add a listener to the window resize event
    spinner?.setAttribute("style", "display: none");
    resizePlayer(player);
    window.onresize = () => {
      if (player) resizePlayer(player);
    };
    if (cb) {
      cb(player);
    }
    (window as StudioPlayerWindow).p = player;
    lastAppliedConfigSignature = runningConfigSignature;
    lastAppliedProjectCacheInputs = runningProjectCacheInputs;
    lastAppliedRecreateCacheInputs = runningRecreateCacheInputs;
  } catch (e) {
    if (
      lifecycleId === sessionLifecycleId &&
      !(e instanceof EditorRecreateStaleError)
    ) {
      spinner?.setAttribute("style", "display: none");
      if (
        runningConfigSignature === pendingConfigSignature &&
        !pendingForceRecreate
      ) {
        oldConfig.onError?.(e);
      }
      console.error("err", e);
    }
    if (lifecycleId === sessionLifecycleId) {
      player = recreateSession?.getPlayer() ?? player;
    }
  }

  // Finally, set the player is being created to false
  if (lifecycleId === sessionLifecycleId) {
    beingCreated = false;
  }
  //#endregion

  if (lifecycleId !== sessionLifecycleId) {
    return;
  }

  // Check the new config and the old config and if they are different, call this function again to recreate the player
  if (
    runningConfigSignature !== pendingConfigSignature ||
    pendingForceRecreate
  ) {
    runPlayer(newConfig);
  }
};

/**
 * Run a debounced runPlayer function.
 */
export const runPlayerDebounce = debounce(
  runPlayer,
  PLAYER_RECREATE_DEBOUNCE_MS,
  {
    trailing: true,
    leading: false,
  }
);

export const destroyPlayerSession = () => {
  sessionLifecycleId += 1;
  runPlayerDebounce.cancel();
  destroyRecreateSession();
  oldConfig = {};
  newConfig = {};
  beingCreated = false;
  lastAppliedProjectCacheInputs = undefined;
  lastAppliedRecreateCacheInputs = undefined;
  lastAppliedConfigSignature = "";
  pendingConfigSignature = "";
  pendingForceRecreate = false;
};
